<template>
  <n-modal
    v-model:show="visible"
    :close-on-esc="true"
    transform-origin="center"
  >
    <n-card
      class="w-40vw min-w-800px max-w-1100px h-50vh"
      header-style="height:58px; padding:12px 18px !important;"
      content-style="height: calc(50vh - 110px);"
      footer-style="height: 62px; padding-top:12px"
      :title="dialogTitle"
      size="small"
      role="dialog"
      aria-modal="true"
    >
      <template #header-extra>
        <n-input
          v-model:value="searchKeyword"
          size="small"
          placeholder="搜索选项"
          clearable
          class="w-10vw min-w-200px max-w-360px"
        >
          <template #prefix>
            <n-icon>
              <Search />
            </n-icon>
          </template>
        </n-input>
      </template>

      <div class="grid gap-x-2" style="grid-template-columns: 240px minmax(300px, 1fr);height: calc(50vh - 110px);">
        <n-card size="small" class="overflow-auto" content-class="!p-8px">
          <n-tree
            block-line
            selectable
            :data="settingsTreeData"
            :expanded-keys="expandedTreeNodeKeys"
            :selected-keys="[selectedTreeNodeKey]"
            :render-switcher-icon="() => null"
            @update:selected-keys="handleSelectedTreeNodeKeysChange"
          />
        </n-card>

        <n-card size="small" class="overflow-auto" content-class="!p-8px">
          <template v-if="isKeyboardShortcutsPanelVisible">
            <n-empty
              v-if="filteredShortcutDefinitions.length === 0"
              description="没有匹配的快捷键"
              size="small"
            />

            <n-table
              v-else
              size="small"
              :bordered="false"
              single-line
              class="shortcut-table"
            >
              <thead>
                <tr>
                  <th>分类</th>
                  <th>快捷键</th>
                  <th>范围</th>
                  <th>行为</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="shortcutDefinition in filteredShortcutDefinitions"
                  :key="`${shortcutDefinition.category}:${shortcutDefinition.shortcut}`"
                >
                  <td>{{ shortcutDefinition.category }}</td>
                  <td>
                    <n-tag size="small" :bordered="false">
                      {{ shortcutDefinition.shortcut }}
                    </n-tag>
                  </td>
                  <td>{{ shortcutDefinition.scope }}</td>
                  <td>{{ shortcutDefinition.action }}</td>
                </tr>
              </tbody>
            </n-table>
          </template>

          <template v-else-if="isSketchRelationsAndSnapPanelVisible">
            <div class="flex flex-col gap-10px">
              <n-checkbox v-model:checked="settingsState.isSketchSnapEnabled">
                激活捕捉(S)
              </n-checkbox>
              <n-checkbox v-model:checked="settingsState.isSnapToModelGeometryEnabled">
                捕捉到模型几何体(Y)
              </n-checkbox>
              <n-checkbox
                :checked="AUTO_GEOMETRY_RELATIONS_ENABLED"
                disabled
              >
                自动几何关系(U)
              </n-checkbox>
            </div>

            <fieldset class="snap-option-group">
              <legend>草图捕捉</legend>

              <n-empty
                v-if="filteredSnapOptionDefinitions.length === 0"
                description="没有匹配的捕捉项"
                size="small"
              />

              <div
                v-for="optionDefinition in filteredSnapOptionDefinitions"
                :key="optionDefinition.key"
                class="snap-option-item"
              >
                <n-checkbox
                  :checked="settingsState.snapOptionState[optionDefinition.key]"
                  @update:checked="(checked) => handleSnapOptionChecked(optionDefinition.key, checked)"
                >
                  {{ optionDefinition.label }}
                </n-checkbox>

                <div
                  v-if="isGridOptionAdditionalSettingsVisible(optionDefinition.key)"
                  class="snap-option-child"
                >
                  <n-checkbox v-model:checked="settingsState.isGridSnapOnlyWhenGridVisibleEnabled">
                    只在网格线显示时捕捉(W)
                  </n-checkbox>
                </div>

                <div
                  v-if="isAngleOptionAdditionalSettingsVisible(optionDefinition.key)"
                  class="snap-option-child angle-option-child"
                >
                  <n-text depth="3">捕捉角度:</n-text>
                  <n-input-number
                    v-model:value="settingsState.angleSnapDegree"
                    size="small"
                    :step="1"
                    :min="1"
                    :max="180"
                  >
                    <template #suffix>
                      度
                    </template>
                  </n-input-number>
                </div>
              </div>
            </fieldset>
          </template>
        </n-card>
      </div>

      <template #footer>
        <div class="flex justify-end gap-15px">
          <n-button @click="handleCancelButtonClick">取消</n-button>
          <n-button type="primary" @click="handleConfirmButtonClick">确定</n-button>
        </div>
      </template>
    </n-card>
  </n-modal>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import type { TreeOption } from 'naive-ui';
import { Search } from '@vicons/carbon';
import {
  AUTO_GEOMETRY_RELATIONS_ENABLED,
  normalizePointSnapSettings,
  PointSnapStrategyType,
} from '@puppycad/cad-core';
import {
  createDefaultSketchRelationsAndSnapSettings,
  EDITOR_SHORTCUT_DEFINITIONS,
  SettingsTreeNodeKey,
  SKETCH_SNAP_OPTION_DEFINITIONS,
  type SketchRelationsAndSnapSettings,
} from '@/config/editor-settings/EditorSettings';

defineOptions({
  name: 'EditorSettingsDialog',
});

const props = withDefaults(defineProps<{
  /**
   * 是否显示弹窗。
   */
  show?: boolean;
  /**
   * 初始捕捉配置；传入时将覆盖默认值，用于从项目恢复设置。
   */
  initialSettings?: SketchRelationsAndSnapSettings | null;
}>(), {
  show: false,
});

const emit = defineEmits<{
  /**
   * 双向绑定显隐状态。
   */
  'update:show': [value: boolean];
  /**
   * 点击“确定”后输出设置快照。
   */
  confirm: [settings: SketchRelationsAndSnapSettings];
}>();

const visible = ref<boolean>(props.show);
const searchKeyword = ref<string>('');
const selectedTreeNodeKey = ref<SettingsTreeNodeKey>(SettingsTreeNodeKey.SketchRelationsAndSnap);
const settingsState = reactive<SketchRelationsAndSnapSettings>(createDefaultSketchRelationsAndSnapSettings());

const dialogTitle = computed(() => {
  if (selectedTreeNodeKey.value === SettingsTreeNodeKey.KeyboardShortcuts) {
    return '系统选项 - 快捷键';
  }

  return '系统选项 - 几何关系/捕捉';
});

const settingsTreeData = computed<TreeOption[]>(() => {
  return [
    {
      key: SettingsTreeNodeKey.KeyboardShortcuts,
      label: '快捷键',
    },
    {
      key: SettingsTreeNodeKey.SketchRoot,
      label: '草图',
      children: [
        {
          key: SettingsTreeNodeKey.SketchRelationsAndSnap,
          label: '几何关系/捕捉',
        },
      ],
    },
  ];
});
const expandedTreeNodeKeys: SettingsTreeNodeKey[] = [
  SettingsTreeNodeKey.SketchRoot,
];

const filteredSnapOptionDefinitions = computed(() => {
  const keyword = searchKeyword.value.trim();

  // 关键字为空时返回完整列表。
  if (keyword === '') {
    return SKETCH_SNAP_OPTION_DEFINITIONS;
  }

  // 根据中文展示文案做包含过滤。
  return SKETCH_SNAP_OPTION_DEFINITIONS.filter((optionDefinition) => {
    return optionDefinition.label.includes(keyword);
  });
});

const filteredShortcutDefinitions = computed(() => {
  const keyword = searchKeyword.value.trim();

  // 快捷键页复用顶部搜索框，避免为设置弹窗新增额外状态。
  if (keyword === '') {
    return EDITOR_SHORTCUT_DEFINITIONS;
  }

  return EDITOR_SHORTCUT_DEFINITIONS.filter((shortcutDefinition) => {
    return [
      shortcutDefinition.category,
      shortcutDefinition.shortcut,
      shortcutDefinition.scope,
      shortcutDefinition.action,
    ].some((text) => text.includes(keyword));
  });
});

const isKeyboardShortcutsPanelVisible = computed(() => {
  return selectedTreeNodeKey.value === SettingsTreeNodeKey.KeyboardShortcuts;
});

const isSketchRelationsAndSnapPanelVisible = computed(() => {
  return selectedTreeNodeKey.value === SettingsTreeNodeKey.SketchRelationsAndSnap;
});

watch(
  () => props.show,
  (newVisible) => {
    // 外部 show 更新时，同步到内部可见状态。
    visible.value = newVisible;
    // 弹窗打开时，若传入了初始配置则恢复，否则使用默认值。
    if (newVisible) {
      const source = props.initialSettings ?? createDefaultSketchRelationsAndSnapSettings();
      // 旧项目可能保存过关闭值，弹窗状态进入 UI 前必须执行同一产品级归一化。
      Object.assign(settingsState, normalizePointSnapSettings(source));
    }
  },
);

watch(visible, (newVisible) => {
  // 内部可见状态变更后向外同步。
  emit('update:show', newVisible);
});

/**
 * 处理设置树选中项变化。
 *
 * @param selectedKey 当前选中键。
 */
function handleSelectedSettingsNode(selectedKey: string | number): void {
  // 菜单键仅支持字符串，非字符串键直接抛错。
  if (typeof selectedKey !== 'string') {
    throw new Error(`设置节点键类型错误: ${String(selectedKey)}`);
  }

  // 仅允许已声明的菜单键，未知键直接抛错。
  if (
    selectedKey !== SettingsTreeNodeKey.KeyboardShortcuts
    && selectedKey !== SettingsTreeNodeKey.SketchRoot
    && selectedKey !== SettingsTreeNodeKey.SketchRelationsAndSnap
  ) {
    throw new Error(`未识别的设置节点键: ${selectedKey}`);
  }

  // 根节点仅用于分组，点击后自动定位到首个可编辑页面。
  if (selectedKey === SettingsTreeNodeKey.SketchRoot) {
    selectedTreeNodeKey.value = SettingsTreeNodeKey.SketchRelationsAndSnap;
    return;
  }

  selectedTreeNodeKey.value = selectedKey as SettingsTreeNodeKey;
}

/**
 * 处理树组件选中节点变化。
 *
 * @param selectedKeys 当前树选中键列表。
 */
function handleSelectedTreeNodeKeysChange(selectedKeys: Array<string | number>): void {
  const selectedKey = selectedKeys[0];

  // Naive UI 在重复点击当前节点时可能发出空选中项；保持当前页面不变即可。
  if (selectedKey === undefined) {
    return;
  }

  handleSelectedSettingsNode(selectedKey);
}

/**
 * 处理捕捉选项勾选变化。
 *
 * @param optionKey 捕捉项策略类型键。
 * @param checked 是否勾选。
 */
function handleSnapOptionChecked(optionKey: PointSnapStrategyType, checked: boolean): void {
  // 更新对应捕捉项开关状态。
  settingsState.snapOptionState[optionKey] = checked;

  // 关闭网格捕捉后，同时关闭其从属选项，避免无效状态残留。
  if (optionKey === PointSnapStrategyType.Grid && !checked) {
    settingsState.isGridSnapOnlyWhenGridVisibleEnabled = false;
  }
}

/**
 * 判断是否展示“网格”附加设置。
 *
 * @param optionKey 捕捉项策略类型键。
 * @returns 是否展示“只在网格线显示时捕捉”。
 */
function isGridOptionAdditionalSettingsVisible(optionKey: PointSnapStrategyType): boolean {
  return optionKey === PointSnapStrategyType.Grid && !!settingsState.snapOptionState[PointSnapStrategyType.Grid];
}

/**
 * 判断是否展示“角度”附加设置。
 *
 * @param optionKey 捕捉项策略类型键。
 * @returns 是否展示“捕捉角度”。
 */
function isAngleOptionAdditionalSettingsVisible(optionKey: PointSnapStrategyType): boolean {
  return optionKey === PointSnapStrategyType.Angle && !!settingsState.snapOptionState[PointSnapStrategyType.Angle];
}

/**
 * 处理取消按钮点击。
 */
function handleCancelButtonClick(): void {
  // 直接关闭弹窗，不提交数据。
  visible.value = false;
}

/**
 * 处理确定按钮点击。
 */
function handleConfirmButtonClick(): void {
  const snapshot: SketchRelationsAndSnapSettings = {
    isSketchSnapEnabled: settingsState.isSketchSnapEnabled,
    isSnapToModelGeometryEnabled: settingsState.isSnapToModelGeometryEnabled,
    // 即使状态被外部脚本修改，设置确认出口仍必须保持自动几何关系开启。
    isAutoGeometryRelationsEnabled: AUTO_GEOMETRY_RELATIONS_ENABLED,
    snapOptionState: {
      ...settingsState.snapOptionState,
    },
    isGridSnapOnlyWhenGridVisibleEnabled: settingsState.isGridSnapOnlyWhenGridVisibleEnabled,
    angleSnapDegree: settingsState.angleSnapDegree,
  };

  // 先输出设置快照，再关闭弹窗。
  emit('confirm', snapshot);
  visible.value = false;
}
</script>

<style scoped lang="less">
.n-card :deep(.n-tree-node-switcher) {
  display: none;
}

.shortcut-table {
  width: 100%;
}

.shortcut-table :deep(th),
.shortcut-table :deep(td) {
  vertical-align: top;
}

.snap-option-group {
  margin-top: 14px;
  border: 1px solid var(--n-border-color);
  border-radius: 4px;
  padding: 10px 12px;
}

.snap-option-group > legend {
  padding: 0 4px;
  font-size: 13px;
}

.snap-option-item {
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.snap-option-item:last-child {
  margin-bottom: 0;
}

.snap-option-child {
  margin-top: 6px;
  margin-left: 24px;
}

.angle-option-child {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
